import { supabase } from "@/integrations/supabase/client";

export type Retention = "session" | "30d" | "90d";
export type RehearsalMode = "home" | "office";
export type Template = "opening" | "rebuttal" | "closing";

export interface RetentionEventPayload {
  retention: Retention;
  mode?: RehearsalMode;
  template?: Template;
  source?: string;
}

/**
 * Fire-and-forget telemetry write for retention-toggle adoption.
 * Anon-safe (RLS allows anonymous INSERT). Never throws — telemetry must
 * not break user flow.
 */
export async function recordRetentionEvent(
  payload: RetentionEventPayload,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from("self_debate_retention_events")
      .insert({
        retention: payload.retention,
        mode: payload.mode ?? null,
        template: payload.template ?? null,
        source: payload.source ?? "self-debate-preview",
      });
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "telemetry failed",
    };
  }
}

export interface RetentionMetric {
  retention: Retention;
  total: number;
  last_7d: number;
  last_30d: number;
  last_event_at: string | null;
}

export async function fetchRetentionMetrics(): Promise<RetentionMetric[]> {
  const { data, error } = await supabase.rpc(
    "get_self_debate_retention_metrics",
  );
  if (error) throw new Error(error.message);
  return (data ?? []) as RetentionMetric[];
}

/** Best-effort rate-limit ping. Fire-and-forget — never blocks reads. */
export async function bumpMetricsRateLimit(): Promise<void> {
  try {
    await supabase.rpc("bump_metrics_rate_limit");
  } catch {
    /* swallow — rate-limit is best-effort */
  }
}

export interface RetentionAuditRow {
  id: string;
  retention_event_id: string | null;
  retention: Retention;
  mode: RehearsalMode | null;
  template: Template | null;
  source: string | null;
  user_id: string | null;
  actor_role: string;
  payload_hash: string;
  created_at: string;
}

export async function fetchRetentionAuditTrail(limit = 200): Promise<RetentionAuditRow[]> {
  const { data, error } = await supabase.rpc("get_retention_audit_trail", { p_limit: limit });
  if (error) throw new Error(error.message);
  return (data ?? []) as RetentionAuditRow[];
}

export interface RetentionAuditExportLogRow {
  id: string;
  exported_by: string | null;
  exported_at: string;
  row_count: number;
  retention_filter: string | null;
  date_from: string | null;
  date_to: string | null;
  search_term: string | null;
  filename: string | null;
  actor_role: string;
}

/**
 * Server-side audit log for CSV exports of the retention audit trail.
 * Sovereign-tier callers only — non-sovereign callers receive 'forbidden'.
 */
export async function logRetentionAuditExport(args: {
  rowCount: number;
  retentionFilter?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  searchTerm?: string | null;
  filename?: string | null;
}): Promise<{ ok: boolean; id?: string; error?: string }> {
  try {
    const { data, error } = await supabase.rpc("log_retention_audit_export", {
      p_row_count: args.rowCount,
      p_retention_filter: args.retentionFilter ?? null,
      p_date_from: args.dateFrom ?? null,
      p_date_to: args.dateTo ?? null,
      p_search_term: args.searchTerm ?? null,
      p_filename: args.filename ?? null,
    });
    if (error) return { ok: false, error: error.message };
    return { ok: true, id: data as unknown as string };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "audit log failed" };
  }
}

export async function fetchRetentionAuditExportLog(limit = 200): Promise<RetentionAuditExportLogRow[]> {
  const { data, error } = await supabase.rpc("get_retention_audit_export_log", { p_limit: limit });
  if (error) throw new Error(error.message);
  return (data ?? []) as RetentionAuditExportLogRow[];
}